<template>
  <div class="usage-panel" v-if="signedIn">
    <p class="usage-title mb-1">
      <strong><i class="fas fa-chart-line"></i> Today's Usage</strong>
      <span class="usage-badge" :title="phaseTooltip">Observing</span>
    </p>

    <!-- Headline numbers — credits + bytes used today, side by side -->
    <p class="usage-headline mb-1">
      <span class="usage-credits">{{ creditsToday | round1 }}</span>
      <span class="usage-credits-unit">credits today</span>
    </p>
    <p v-if="bytesToday > 0" class="usage-headline-sub mb-1">
      <span class="usage-bytes">{{ bytesToday | formatBytes }}</span>
      <span class="usage-credits-unit">uploaded today</span>
    </p>

    <!-- Per-operation breakdown — only shown when there's something to see -->
    <ul v-if="hasBreakdown" class="usage-breakdown">
      <li
        v-for="row in breakdownRows"
        :key="row.operation"
      >
        <span class="op-label">{{ row.label }}</span>
        <span class="op-count">
          {{ row.count }}×
          <span v-if="row.credits > 0">· {{ row.credits | round1 }} cr</span>
          <span v-if="row.bytes > 0">· {{ row.bytes | formatBytes }}</span>
        </span>
      </li>
    </ul>
    <p v-else class="usage-empty text-muted small mb-0">
      No tracked operations today. Indexing satellite scenes, running analyses, or
      copying datasets all count toward your daily total.
    </p>

    <!-- Footer — explains the system honestly -->
    <p class="usage-foot text-muted">
      We're observing usage right now without any limit. Once we have a clearer
      picture of how the platform is used, free-tier and paid-tier daily limits
      will be set transparently.
      <span class="usage-lifetime" v-if="totalCredits != null">
        · Lifetime: {{ totalCredits | round0 }} credits across {{ totalOperations || 0 }} operations<span v-if="totalBytesLifetime"> · {{ totalBytesLifetime | formatBytes }} uploaded</span>
      </span>
    </p>
  </div>
</template>

<script>
/* eslint-disable no-console */
const fb = require('../../firebaseConfig.js')

// Display labels for each operation key. Keep in sync with CREDIT_COSTS
// keys in functions/usage.js. Anything not listed here renders with a
// fallback (Title Case'd version of the snake_case key).
const OPERATION_LABELS = {
  satellite_raster_save: 'Satellite raster saves',
  recipe_single_scene: 'Single-scene index analyses',
  recipe_temporal_mean: 'Temporal aggregations',
  recipe_raster_ingest: 'Raster ingests (NAIP, DEM, etc.)',
  copy_dataset: 'Dataset copies',
  magic_map: 'Magic Map interpolations',
  dataset_upload: 'Dataset / GIS file uploads',
  image_upload: 'Image uploads',
  collector_create: 'Data collector created',
  collector_update: 'Data collector schema updates',
  collector_recompile: 'Data collector recompiles',
  collector_datapoints_push: 'Datapoints pushed (API)'
}

function utcDayKey () {
  const d = new Date()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

export default {
  name: 'UsagePanel',
  data () {
    return {
      // Reactive snapshots from the two Firestore docs we listen to.
      lifetime: null,
      today: null
    }
  },
  // Non-reactive instance properties — listener detach handles. Vue 2
  // reserves `_`-prefixed keys on the data() return value, so detach
  // closures live on `this` directly via `created()` / `mounted()`.
  created () {
    this.detachLifetime = null
    this.detachToday = null
  },
  computed: {
    signedIn () {
      return !!(this.$store.state.currentUser && this.$store.state.currentUser.uid)
    },
    creditsToday () {
      return (this.today && this.today.credits) || 0
    },
    bytesToday () {
      return (this.today && this.today.bytesUploaded) || 0
    },
    totalBytesLifetime () {
      return this.lifetime ? this.lifetime.totalBytesUploaded : null
    },
    totalCredits () {
      return this.lifetime ? this.lifetime.totalCredits : null
    },
    totalOperations () {
      return this.lifetime ? this.lifetime.totalOperations : null
    },
    hasBreakdown () {
      return this.breakdownRows.length > 0
    },
    breakdownRows () {
      const by = (this.today && this.today.byOperation) || {}
      return Object.keys(by)
        .map(op => ({
          operation: op,
          label: OPERATION_LABELS[op] || op.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          credits: (by[op] && by[op].credits) || 0,
          count: (by[op] && by[op].count) || 0,
          bytes: (by[op] && by[op].bytes) || 0
        }))
        .filter(r => r.credits > 0 || r.count > 0 || r.bytes > 0)
        // Sort by credits, but credit-free byte-only operations (image
        // uploads) get ranked by byte total so they still appear in a
        // reasonable place.
        .sort((a, b) => (b.credits - a.credits) || (b.bytes - a.bytes))
    },
    phaseTooltip () {
      return 'Phase 1 — usage is recorded but no daily limits are enforced yet.'
    }
  },
  filters: {
    round1 (n) {
      const v = Number(n) || 0
      return Math.round(v * 10) / 10
    },
    round0 (n) {
      return Math.round(Number(n) || 0)
    },
    // Human-readable bytes with one decimal — KB / MB / GB. Keeps the
    // panel readable when a user uploads a multi-megapixel image batch.
    formatBytes (n) {
      const v = Number(n) || 0
      if (v < 1024) return v + ' B'
      if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB'
      if (v < 1024 * 1024 * 1024) return (v / (1024 * 1024)).toFixed(1) + ' MB'
      return (v / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
    }
  },
  mounted () {
    this.subscribe()
  },
  beforeDestroy () {
    this.unsubscribe()
  },
  watch: {
    '$store.state.currentUser.uid' () {
      // Account switch — re-bind to the new user's docs.
      this.unsubscribe()
      this.subscribe()
    }
  },
  methods: {
    subscribe () {
      const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
      if (!uid) return
      const userRef = fb.db.collection('userUsage').doc(uid)
      const dayRef = userRef.collection('days').doc(utcDayKey())

      this.detachLifetime = userRef.onSnapshot(snap => {
        this.lifetime = snap.exists ? snap.data() : null
      }, err => {
        // Phase-1 docs may not exist yet for a brand-new user; that's
        // fine — we render zeroes and the doc gets created on first
        // chargeable operation.
        console.warn('[usage] lifetime listener:', err && err.message)
      })

      this.detachToday = dayRef.onSnapshot(snap => {
        this.today = snap.exists ? snap.data() : null
      }, err => {
        console.warn('[usage] today listener:', err && err.message)
      })
    },
    unsubscribe () {
      if (this.detachLifetime) { try { this.detachLifetime() } catch (e) {} this.detachLifetime = null }
      if (this.detachToday) { try { this.detachToday() } catch (e) {} this.detachToday = null }
    }
  }
}
</script>

<style scoped>
.usage-panel {
  border-top: 1px solid #e5e9f0;
  padding-top: 12px;
  margin-top: 12px;
  font-size: 0.88rem;
}
.usage-title { display: flex; align-items: center; gap: 8px; }
.usage-title i { color: #0b5ed7; }
.usage-badge {
  background: #fff8e1;
  color: #8a6d00;
  border: 1px solid #ffe58f;
  font-size: 0.68rem;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: help;
}
.usage-headline { font-size: 0.92rem; }
.usage-credits {
  font-size: 1.4rem;
  font-weight: 700;
  color: #177045;
  margin-right: 6px;
}
.usage-bytes {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0b5ed7;
  margin-right: 6px;
}
.usage-headline-sub { font-size: 0.85rem; }
.usage-credits-unit { color: #555; font-weight: 500; }
.usage-breakdown {
  list-style: none;
  margin: 4px 0 8px;
  padding: 0;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  overflow: hidden;
}
.usage-breakdown li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  border-bottom: 1px solid #eef0f3;
  background: #fafbfc;
}
.usage-breakdown li:last-child { border-bottom: none; }
.op-label { font-size: 0.85rem; }
.op-count { font-size: 0.78rem; color: #555; font-variant-numeric: tabular-nums; }
.usage-empty { font-size: 0.78rem; line-height: 1.4; }
.usage-foot {
  font-size: 0.74rem;
  line-height: 1.45;
  margin: 6px 0 0;
}
.usage-lifetime { display: inline; }
</style>
